// TEMPORARY — MARADDO production forensic diagnostic (READ-ONLY).
// Super Admin only. Calls GET /api/admin/diagnostics/maraddo (no input).
// Remove this page + its route after the investigation is complete.
import { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ShieldAlert, RefreshCw, Search } from 'lucide-react';

const Section = ({ title, count, children }) => (
    <Card className="mb-4">
        <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
                <span>{title}</span>
                <Badge variant="outline">{count}</Badge>
            </CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
    </Card>
);

const Pre = ({ obj }) => (
    <pre className="text-xs bg-muted/40 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
        {JSON.stringify(obj, null, 2)}
    </pre>
);

export default function MaraddoDiagnostic() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const run = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/admin/diagnostics/maraddo');
            setData(res.data);
        } catch (e) {
            setError(e?.response?.status === 403
                ? 'Super Admin access required.'
                : 'Diagnostic request failed.');
        } finally {
            setLoading(false);
        }
    };

    if (!isSuperAdmin) {
        return (
            <div className="max-w-2xl mx-auto py-16 text-center" data-testid="maraddo-forbidden">
                <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <h1 className="text-xl font-semibold">Super Admin only</h1>
                <p className="text-muted-foreground mt-1">This temporary diagnostic is restricted.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-8 px-4" data-testid="maraddo-diagnostic">
            <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Search className="w-5 h-5" /> MARADDO Forensic Trace
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Temporary, read-only. Runs a fixed set of queries — no data is modified.
                    </p>
                </div>
                <Button onClick={run} disabled={loading} data-testid="maraddo-run-btn">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Run diagnostic
                </Button>
            </div>

            {error && (
                <Card className="mb-4 border-red-500/40" data-testid="maraddo-error">
                    <CardContent className="py-6 text-center text-red-600">{error}</CardContent>
                </Card>
            )}

            {data && (
                <div data-testid="maraddo-results">
                    <Section title="Environment (confirm this is PRODUCTION)" count="context">
                        <Pre obj={data.environment} />
                        <p className="text-xs text-muted-foreground mt-2">
                            Search terms: {JSON.stringify(data.search_terms)}
                        </p>
                        <p className="text-xs mt-1">
                            Read-only: <strong>{String(data.read_only)}</strong> · Summary: {JSON.stringify(data.summary)}
                        </p>
                    </Section>

                    <Section title="Organizations" count={data.organizations?.length ?? 0}>
                        {data.organizations?.length
                            ? data.organizations.map((o, i) => <Pre key={i} obj={o} />)
                            : <p className="text-sm text-muted-foreground">No matching organizations.</p>}
                    </Section>

                    <Section title="Cards" count={data.cards?.length ?? 0}>
                        {data.cards?.length
                            ? data.cards.map((c, i) => <Pre key={i} obj={c} />)
                            : <p className="text-sm text-muted-foreground">No matching cards.</p>}
                    </Section>

                    <Section title="Users (Hugh / BC / matched orgs)" count={data.users?.length ?? 0}>
                        {data.users?.length
                            ? data.users.map((u, i) => <Pre key={i} obj={u} />)
                            : <p className="text-sm text-muted-foreground">No matching users.</p>}
                    </Section>

                    <Section title="History / Audit" count={data.history?.length ?? 0}>
                        {data.history?.length
                            ? <Pre obj={data.history} />
                            : <p className="text-sm text-muted-foreground">No audit/history references.</p>}
                    </Section>
                </div>
            )}
        </div>
    );
}
